import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, Modal, ModalFuncProps } from 'antd';

import * as actions from '../store/actions';

import { AppDispatch, RootState } from '../../../store';
import { ChatsCollection, SmsChat } from '../models';
import { ArgsProps } from 'antd/es/notification';
import { Store } from 'antd/es/form/interface';

export type ChangeChatNameModalProps = {
  visible: boolean;
  onClose: () => void;
  smsChatId: string;
  currentName: string;
  updateMessageControl: () => void;
};

const ChangeChatNameModal = ({ visible, onClose, smsChatId, currentName, updateMessageControl }: ChangeChatNameModalProps) => {
  const [filterForm] = Form.useForm();

  const { modal, notification } = App.useApp();

  const dispatch = useDispatch<AppDispatch>();

  const [chatName, setChatName] = useState<string>(currentName);

  const changeChatNameResultLoading = useSelector((state: RootState) => state.smsChat.changeChatNameResultLoading);

  const chatItems = useSelector((state: RootState) => state.smsChat.localChats);

  const onCancel = () => {
    onClose();
  };

  const onOk = () => {
    filterForm.validateFields().then(() => {
      modal.confirm({
        title: 'Are you sure you want to change the chat name?',
        icon: <ExclamationCircleOutlined />,
        okText: 'Yes',
        okType: 'primary',
        cancelText: 'No',
        confirmLoading: changeChatNameResultLoading,
        okButtonProps: { disabled: changeChatNameResultLoading },
        async onOk() {
          await dispatch(
            actions.changeChatName({
              smsChatId,
              chatName: chatName,
            }),
          );

          notification.success({
            message: 'Success',
            top: 55,
            duration: 10,
            description: `Chat name was successfully changed`,
          } as ArgsProps);

          let newChatList = (chatItems?.chatList ?? []).slice();
          let chatListIndex = newChatList.findIndex((x: SmsChat) => x.smsChatId === smsChatId);
          let chatItem = { ...newChatList[chatListIndex] } as SmsChat;

          chatItem.chatName = chatName ?? '';
          newChatList[chatListIndex] = chatItem;

          let newChatItems = { ...chatItems } as ChatsCollection;
          newChatItems.chatList = newChatList;

          dispatch(actions.setLocalChats(newChatItems));
          dispatch(actions.setSelectedChatItem(chatItem));

          updateMessageControl();
          onClose();
        },
      } as ModalFuncProps);
    });
  };

  const onChange = (value: React.ChangeEvent<HTMLInputElement>) => {
    setChatName(value.target.value);
  };

  return (
    <Modal
      open={visible}
      title="Change chat name"
      centered
      onCancel={onCancel}
      onOk={onOk}
      className="change-chat-name-modal"
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={changeChatNameResultLoading} onClick={onOk} disabled={!chatName}>
          Change chat name
        </Button>,
      ]}
    >
      <Form
        form={filterForm}
        size="small"
        layout="horizontal"
        initialValues={
          {
            newChatName: currentName,
          } as Store
        }
      >
        <Form.Item
          name={['newChatName']}
          label="New chat name"
          rules={[
            { required: true, message: 'New chat name is required.' },
            () => ({
              validator(_, value) {
                return value.length > 0 && value.trim().length === 0
                  ? Promise.reject(new Error('The name cannot consist of spaces only.'))
                  : Promise.resolve();
              },
            }),
            () => ({
              validator(_, value) {
                return value.length < 51 ? Promise.resolve() : Promise.reject(new Error('The new chat name must be less than 51 characters long.'));
              },
            }),
          ]}
        >
          <Input
            placeholder="Type chat name"
            value={chatName}
            onChange={onChange}
            onPressEnter={chatName !== null && chatName !== undefined && chatName !== '' ? onOk : undefined}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangeChatNameModal;
